import React, { useState } from 'react';
import { Resume } from '@/api/types';
import { LibraryUpload } from './LibraryUpload';
import { LibraryItem } from './LibraryItem';

interface ResumeLibraryProps {
  resumes: Resume[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onUpload: (fileContentBase64: string, name: string, label: string) => Promise<void>;
}

export const Library = React.memo(function Library({ resumes, selectedId, onSelect, onDelete, onUpload }: ResumeLibraryProps) {
  const [label, setLabel] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = (ev.target?.result as string).split(',')[1];
      if (base64) {
        let finalLabel = label.trim();
        if (!finalLabel) {
          const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
          const dateStr = new Date().toLocaleString(undefined, { 
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
          });
          finalLabel = `${nameWithoutExt} (${dateStr})`;
        }
        await onUpload(base64, file.name, finalLabel);
        setLabel('');
      }
      setUploading(false);
    };

    reader.readAsDataURL(file);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleFile(file);
    if (e.target) e.target.value = '';
  };

  const isMaxResumes = resumes.length >= 10;

  return (
    <div className="flex flex-col shrink-0 bg-white dark:bg-slate-950/50 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden dark:shadow-sm">
      <LibraryUpload 
        label={label}
        setLabel={setLabel}
        uploading={uploading}
        isMaxResumes={isMaxResumes}
        onFileChange={handleFileChange}
        onFileDrop={handleFile}
      />

      <div className="divide-y divide-slate-200 dark:divide-slate-800 overflow-y-auto max-h-[220px]">
        {resumes.length === 0 ? (
          <div className="py-6 text-center text-slate-700 dark:text-slate-400 px-4">
            <p className="text-xs">No resumes uploaded yet.</p>
          </div>
        ) : (
          resumes.map((resume) => (
            <LibraryItem
              key={resume.id}
              resume={resume}
              isSelected={resume.id === selectedId}
              onSelect={onSelect}
              onDelete={onDelete}
            />
          ))
        )}
      </div>
    </div>
  );
});